import { useState, useMemo, useEffect } from 'react';
import { BookOpen, Search, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useMyWorkspaces } from '../../hooks/useWorkspaces';
import { useWorkspaceGlossary } from '../../hooks/useWorkspaceGlossary';
import type { WorkspaceGlossaryEntry } from '../../services/workspaceGlossaryService';
import Spinner from '../ui/Spinner';

const LS_KEY = 'glossary-panel-workspace';

interface GlossaryPanelProps {
  onClose: () => void;
  /** 워크스페이스 변경 시 부모에게 전체 용어 목록 전달 */
  onEntriesChange?: (entries: WorkspaceGlossaryEntry[]) => void;
}

export default function GlossaryPanel({ onClose, onEntriesChange }: GlossaryPanelProps) {
  const { data: workspaces, isLoading: wsLoading } = useMyWorkspaces();

  // localStorage에서 이전 선택값 복원
  const [selectedWsId, setSelectedWsId] = useState(() => {
    return localStorage.getItem(LS_KEY) ?? '';
  });

  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 워크스페이스 목록 로드 시 초기 선택
  useEffect(() => {
    if (!workspaces || workspaces.length === 0) return;
    const cached = localStorage.getItem(LS_KEY);
    const valid = workspaces.some((w) => w.workspaceId === cached);
    if (valid && cached) {
      setSelectedWsId(cached);
    } else {
      setSelectedWsId(workspaces[0].workspaceId);
    }
  }, [workspaces]);

  // 선택 변경 시 localStorage 저장
  useEffect(() => {
    if (selectedWsId) {
      localStorage.setItem(LS_KEY, selectedWsId);
    }
  }, [selectedWsId]);

  // 전체 용어 페치 (limit 큰 값으로 전체)
  const { data: result, isLoading: glossaryLoading } = useWorkspaceGlossary(selectedWsId, {
    limit: 500,
    sortBy: 'term',
    sortOrder: 'asc',
  });

  const allEntries = result?.data ?? [];

  // 부모에게 엔트리 전달 (GlossaryHighlight용)
  useEffect(() => {
    onEntriesChange?.(allEntries);
  }, [allEntries, onEntriesChange]);

  // 클라이언트 사이드 검색 필터
  const filtered = useMemo(() => {
    if (!search.trim()) return allEntries;
    const q = search.toLowerCase();
    return allEntries.filter(
      (e) =>
        e.term.toLowerCase().includes(q) ||
        e.definition.toLowerCase().includes(q) ||
        e.aliases.some((a) => a.toLowerCase().includes(q)),
    );
  }, [allEntries, search]);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, WorkspaceGlossaryEntry[]>();
    for (const entry of filtered) {
      const cat = entry.category || '미분류';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(entry);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, 'ko'));
  }, [filtered]);

  const handleWsChange = (wsId: string) => {
    setSelectedWsId(wsId);
    setSearch('');
    setExpandedId(null);
  };

  return (
    <div className="w-72 bg-white border-l border-gray-200 flex flex-col shrink-0 h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <BookOpen className="size-4 text-primary-600" />
          <span className="text-sm font-semibold text-gray-900">용어 사전</span>
          <span className="text-sm text-gray-400">({allEntries.length})</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Workspace Selector */}
      <div className="px-3 py-2 border-b border-gray-100">
        {wsLoading ? (
          <div className="flex items-center justify-center py-1">
            <Spinner className="size-4" />
          </div>
        ) : workspaces && workspaces.length > 0 ? (
          <select
            value={selectedWsId}
            onChange={(e) => handleWsChange(e.target.value)}
            className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none bg-gray-50"
          >
            {workspaces.map((ws) => (
              <option key={ws.workspaceId} value={ws.workspaceId}>
                {ws.name}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-gray-400 text-center py-1">소속 워크스페이스 없음</p>
        )}
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="용어 검색..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
      </div>

      {/* Term list */}
      <div className="flex-1 overflow-y-auto">
        {glossaryLoading ? (
          <div className="flex items-center justify-center h-32">
            <Spinner className="size-6" />
          </div>
        ) : grouped.length > 0 ? (
          grouped.map(([category, items]) => (
            <div key={category}>
              <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-gray-500 uppercase tracking-wider sticky top-0">
                {category}
              </div>
              {items.map((entry) => (
                <button
                  key={entry.glossaryId}
                  onClick={() =>
                    setExpandedId(expandedId === entry.glossaryId ? null : entry.glossaryId)
                  }
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50"
                >
                  <div className="flex items-center gap-1.5">
                    {expandedId === entry.glossaryId ? (
                      <ChevronDown className="size-3 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronRight className="size-3 text-gray-400 shrink-0" />
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {entry.term}
                    </span>
                  </div>
                  {expandedId === entry.glossaryId && (
                    <div className="mt-1.5 ml-4.5 space-y-1">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {entry.definition}
                      </p>
                      {entry.aliases.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {entry.aliases.map((alias, i) => (
                            <span
                              key={i}
                              className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-[11px] text-gray-500"
                            >
                              {alias}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <BookOpen className="size-6 mb-2" />
            <p className="text-sm">
              {search ? '검색 결과가 없습니다.' : '등록된 용어가 없습니다.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
